import React, { useEffect, useState } from "react";
import { ScrollText, Loader2, Search, Database, DownloadCloud, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../../components/ui/input";

const RESOURCE_COLORS = {
  customer: "#3b82f6",
  job: "#f28a16",
  testimonial: "#10b981",
  system: "#6b7280",
  default: "#20282c",
};

const ACTION_LABELS = {
  export: "Exported data",
  erase: "Erased customer",
  approve: "Approved",
  archive: "Archived",
  restore: "Restored",
  delete: "Deleted",
  void: "Voided",
  unvoid: "Un-voided",
  backup: "Backup taken",
};

const fmtBytes = (b) => {
  if (!b) return "—";
  const kb = b / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

export const AdminAuditLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [resource, setResource] = useState("");
  const [backups, setBackups] = useState([]);
  const [runningBackup, setRunningBackup] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [logs, bks] = await Promise.all([
        adminApi.auditLog({ ...(q ? { q } : {}), ...(resource ? { resource_type: resource } : {}) }),
        adminApi.listBackups(),
      ]);
      setRows(logs);
      setBackups(bks.backups || []);
    } catch { toast.error("Could not load audit log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const runBackupNow = async () => {
    setRunningBackup(true);
    try { const r = await adminApi.runBackup(); toast.success(`Backup ${r.stamp} completed`); load(); }
    catch { toast.error("Backup failed"); } finally { setRunningBackup(false); }
  };

  return (
    <div data-testid="admin-audit-log-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Audit log &amp; backups</h1>
      <p className="text-[#3f4b52] mt-1">POPIA-friendly trace of every consequential action + the daily automated backup archive.</p>

      {/* Backups panel */}
      <section className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] inline-flex items-center gap-1.5">
              <Database size={12} /> Backups
            </div>
            <div className="font-display font-bold text-[#111619] mt-0.5">Daily 02:00 SAST · retention 14 days</div>
            <p className="text-xs text-[#3f4b52] mt-1">JSON dumps of every collection. You can also trigger a snapshot right now.</p>
          </div>
          <button
            onClick={runBackupNow}
            disabled={runningBackup}
            data-testid="backup-run-btn"
            className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-60"
          >
            {runningBackup ? <Loader2 className="animate-spin" size={14} /> : <DownloadCloud size={14} />}
            {runningBackup ? "Running…" : "Run backup now"}
          </button>
        </div>
        {backups.length > 0 && (
          <div className="mt-4 max-h-56 overflow-y-auto border border-[#eef2f4] rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-[#f5f6f6] text-[#3f4b52] uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Stamp</th>
                  <th className="text-left px-3 py-2">Files</th>
                  <th className="text-right px-3 py-2">Size</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {backups.map((b) => (
                  <tr key={b.stamp}>
                    <td className="px-3 py-2 font-mono text-[#111619]">{b.stamp}</td>
                    <td className="px-3 py-2 text-[#3f4b52]">{b.files.length} collections</td>
                    <td className="px-3 py-2 text-right">{fmtBytes(b.size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Audit log */}
      <section className="mt-6 bg-white border border-[#cfd7db] rounded-md">
        <div className="p-4 flex items-center gap-3 flex-wrap border-b border-[#eef2f4]">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Audit trail
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search actor / action / id…"
                data-testid="audit-search"
                className="pl-9 bg-white border-[#cfd7db] h-9 w-64"
              />
            </div>
            <select
              value={resource}
              onChange={(e) => setResource(e.target.value)}
              className="h-9 px-3 bg-white border border-[#cfd7db] rounded-md text-sm"
              data-testid="audit-resource-filter"
            >
              <option value="">All resources</option>
              <option value="customer">Customer</option>
              <option value="job">Job</option>
              <option value="testimonial">Testimonial</option>
              <option value="system">System</option>
            </select>
            <button onClick={load} className="bg-[#20282c] text-white px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px]">Apply</button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#3f4b52]">
            <ScrollText size={28} className="mx-auto text-[#9aa6ad]" />
            <p className="mt-2 text-sm">No audit events for these filters.</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Actor</th>
                  <th className="text-left px-4 py-2">Action</th>
                  <th className="text-left px-4 py-2">Resource</th>
                  <th className="text-left px-4 py-2">ID</th>
                  <th className="text-left px-4 py-2">Meta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]" data-testid="audit-rows">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 text-xs text-[#3f4b52] whitespace-nowrap">{new Date(r.at).toLocaleString("en-ZA")}</td>
                    <td className="px-4 py-2 text-xs text-[#111619] font-semibold">{r.actor}</td>
                    <td className="px-4 py-2 text-xs">{ACTION_LABELS[r.action] || r.action}</td>
                    <td className="px-4 py-2 text-xs">
                      <span
                        className="px-1.5 py-0.5 rounded-sm text-white font-display uppercase tracking-widest text-xs"
                        style={{ background: RESOURCE_COLORS[r.resource_type] || RESOURCE_COLORS.default }}
                      >
                        {r.resource_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-[#3f4b52] truncate max-w-[240px]">{r.resource_id || "—"}</td>
                    <td className="px-4 py-2 text-xs text-[#3f4b52] font-mono truncate max-w-[240px]">
                      {r.meta && Object.keys(r.meta).length > 0 ? JSON.stringify(r.meta) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAuditLog;
